/**
 * One-off device snapshot: traits a fingerprint is built from, plus traces that
 * automation (headless browsers, CDP drivers, extensions) tends to leave.
 * Each probe is wrapped so one failure doesn't lose the rest.
 */

const safe = <T>(fn: () => T): T | { err: string } => {
  try {
    return fn();
  } catch (e) {
    return { err: e instanceof Error ? e.name : 'error' };
  }
};

const safeAsync = async <T>(fn: () => Promise<T>, timeoutMs = 1500): Promise<T | { err: string }> => {
  try {
    return await Promise.race([
      fn(),
      new Promise<{ err: string }>((resolve) => setTimeout(() => resolve({ err: 'timeout' }), timeoutMs)),
    ]);
  } catch (e) {
    return { err: e instanceof Error ? e.name : 'error' };
  }
};

/** Short non-cryptographic hash, for comparing canvas output without sending it. */
const fnv1a = (s: string): string => {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return (h >>> 0).toString(16);
};

const AUTOMATION_GLOBALS = [
  '__playwright__binding__', '__pwInitScripts', '__playwright_evaluation_script__',
  '__puppeteer_evaluation_script__', '__nightmare', '_phantom', 'callPhantom',
  '__selenium_unwrapped', '__webdriver_evaluate', '__driver_evaluate',
  '__webdriver_script_fn', '__fxdriver_unwrapped', '_Selenium_IDE_Recorder',
  'domAutomation', 'domAutomationController', '__lastWatirAlert', 'awesomium',
];

const automationTraces = () => {
  const w = window as unknown as Record<string, unknown>;
  const globals = AUTOMATION_GLOBALS.filter((k) => k in w);
  // ChromeDriver injects cdc_* properties onto window/document
  const cdc = [...Object.keys(w), ...Object.keys(document)].filter((k) => /^\$?cdc_|\$wdc_/.test(k));
  return {
    webdriver: navigator.webdriver,
    globals,
    cdc,
    docAttrs: ['webdriver', 'selenium', 'driver'].filter((a) =>
      document.documentElement.hasAttribute(a),
    ),
  };
};

/**
 * A DevTools-protocol client with the Runtime domain enabled (Puppeteer, Playwright,
 * many agent drivers, and also an open DevTools window) serialises console arguments,
 * which reads the error's `stack` property.
 */
const cdpRuntimeProbe = (): Promise<boolean> =>
  new Promise((resolve) => {
    let hit = false;
    try {
      const e = new Error('ac');
      Object.defineProperty(e, 'stack', {
        configurable: true,
        get() {
          hit = true;
          return '';
        },
      });
      // eslint-disable-next-line no-console
      console.debug(e);
    } catch {
      /* ignore */
    }
    setTimeout(() => resolve(hit), 0);
  });

const webgl = () => {
  const canvas = document.createElement('canvas');
  const gl = (canvas.getContext('webgl') || canvas.getContext('experimental-webgl')) as WebGLRenderingContext | null;
  if (!gl) return null;
  const ext = gl.getExtension('WEBGL_debug_renderer_info');
  return {
    vendor: ext ? gl.getParameter(ext.UNMASKED_VENDOR_WEBGL) : gl.getParameter(gl.VENDOR),
    renderer: ext ? gl.getParameter(ext.UNMASKED_RENDERER_WEBGL) : gl.getParameter(gl.RENDERER),
    maxTexture: gl.getParameter(gl.MAX_TEXTURE_SIZE),
  };
};

const canvasHash = () => {
  const c = document.createElement('canvas');
  c.width = 220;
  c.height = 30;
  const ctx = c.getContext('2d');
  if (!ctx) return null;
  ctx.textBaseline = 'top';
  ctx.font = '14px Arial';
  ctx.fillStyle = '#f60';
  ctx.fillRect(100, 1, 62, 20);
  ctx.fillStyle = '#069';
  ctx.fillText('agentcatch, \u{1F600}', 2, 15);
  ctx.fillStyle = 'rgba(102, 204, 0, 0.7)';
  ctx.fillText('agentcatch, \u{1F600}', 4, 17);
  return fnv1a(c.toDataURL());
};

const FONT_PROBES = [
  'Arial', 'Helvetica Neue', 'Segoe UI', 'Roboto', 'Ubuntu', 'DejaVu Sans',
  'Liberation Sans', 'Noto Sans', 'San Francisco', 'Menlo', 'Consolas', 'Calibri',
];

/** Which common fonts are installed, by comparing text widths with fallbacks. */
const fonts = () => {
  const c = document.createElement('canvas').getContext('2d');
  if (!c) return null;
  const sample = 'mmmmmmmmmmlli1WQ';
  const width = (font: string) => {
    c.font = `72px ${font}`;
    return c.measureText(sample).width;
  };
  const bases = ['monospace', 'sans-serif', 'serif'].map((b) => [b, width(b)] as const);
  return FONT_PROBES.filter((f) => bases.some(([b, w]) => width(`"${f}", ${b}`) !== w));
};

const permissionsMismatch = async () => {
  if (!navigator.permissions || typeof Notification === 'undefined') return null;
  const status = await navigator.permissions.query({ name: 'notifications' as PermissionName });
  // Headless Chrome historically reports Notification "denied" while the query says "prompt"
  return { notification: Notification.permission, query: status.state };
};

const mediaDevices = async () => {
  if (!navigator.mediaDevices?.enumerateDevices) return null;
  const devices = await navigator.mediaDevices.enumerateDevices();
  const counts: Record<string, number> = {};
  for (const d of devices) counts[d.kind] = (counts[d.kind] ?? 0) + 1;
  return counts;
};

const uaData = async () => {
  const d = (navigator as unknown as { userAgentData?: any }).userAgentData;
  if (!d) return null;
  const high = await d.getHighEntropyValues([
    'architecture', 'bitness', 'model', 'platformVersion', 'fullVersionList',
  ]);
  return { brands: d.brands, mobile: d.mobile, platform: d.platform, ...high };
};

export const collectDevice = async (): Promise<Record<string, unknown>> => {
  const nav = navigator as Navigator & { deviceMemory?: number };
  const [cdp, perms, media, ua] = await Promise.all([
    safeAsync(cdpRuntimeProbe),
    safeAsync(permissionsMismatch),
    safeAsync(mediaDevices),
    safeAsync(uaData),
  ]);
  return {
    ua: nav.userAgent,
    uaData: ua,
    platform: nav.platform,
    vendor: nav.vendor,
    languages: safe(() => [...nav.languages]),
    hardwareConcurrency: nav.hardwareConcurrency,
    deviceMemory: nav.deviceMemory,
    maxTouchPoints: nav.maxTouchPoints,
    pdfViewerEnabled: (nav as unknown as { pdfViewerEnabled?: boolean }).pdfViewerEnabled,
    cookieEnabled: nav.cookieEnabled,
    plugins: safe(() => nav.plugins.length),
    mimeTypes: safe(() => nav.mimeTypes.length),
    timeZone: safe(() => Intl.DateTimeFormat().resolvedOptions().timeZone),
    tzOffset: new Date().getTimezoneOffset(),
    locale: safe(() => Intl.DateTimeFormat().resolvedOptions().locale),
    screen: safe(() => ({
      w: screen.width, h: screen.height, aw: screen.availWidth, ah: screen.availHeight,
      cd: screen.colorDepth, dpr: window.devicePixelRatio,
    })),
    // Headless and some VM kiosks report outer == inner, or 0
    window: windowSize(),
    hasChrome: 'chrome' in window,
    hasChromeRuntime: safe(() => !!(window as any).chrome?.runtime),
    webgl: safe(webgl),
    canvas: safe(canvasHash),
    fonts: safe(fonts),
    voices: safe(() => (typeof speechSynthesis !== 'undefined' ? speechSynthesis.getVoices().length : null)),
    permissions: perms,
    mediaDevices: media,
    automation: safe(automationTraces),
    cdpRuntime: cdp,
  };
};

/**
 * Window size and whether the page was visible when measured: Chrome reports a 0x0 outer
 * window for a page that hasn't been shown yet (background tab, prerender).
 */
export const windowSize = () => ({
  iw: window.innerWidth, ih: window.innerHeight, ow: window.outerWidth, oh: window.outerHeight,
  vis: document.visibilityState,
});

/**
 * Attributes and extra elements on <html>/<body> that the page didn't put there.
 * Browser extensions, including agent extensions, often inject some.
 */
export const domIntrusions = (baseline: { html: string[]; body: string[] }) => {
  const attrs = (el: Element | null) => (el ? el.getAttributeNames() : []);
  const added = (now: string[], before: string[]) => now.filter((a) => !before.includes(a));
  const outsideBody = [...document.documentElement.children]
    .filter((el) => el !== document.head && el !== document.body)
    .map((el) => el.tagName.toLowerCase());
  return {
    htmlAttrs: added(attrs(document.documentElement), baseline.html),
    bodyAttrs: added(attrs(document.body), baseline.body),
    outsideBody,
  };
};
